import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { setBackendBaseUrl } from '../utils/backendUrl';

type OperatorConfig = {
  operatorName: string;
  backendUrl: string;
  livekitUrl: string;
};

type OperatorConfigContextValue = {
  config: OperatorConfig;
  defaults: OperatorConfig;
  updateConfig: (update: Partial<OperatorConfig>) => void;
  resetConfig: () => void;
};

const STORAGE_KEY = 'ares-operator-console:config';

function createDefaults(): OperatorConfig {
  const operatorName = import.meta.env.VITE_OPERATOR_NAME?.trim();
  const backendUrl = import.meta.env.VITE_BACKEND_URL?.trim();
  const livekitUrl = import.meta.env.VITE_LIVEKIT_URL?.trim();

  return {
    operatorName: operatorName && operatorName.length > 0 ? operatorName : 'Operator',
    backendUrl: backendUrl ?? '',
    livekitUrl: livekitUrl ?? '',
  };
}

const OperatorConfigContext = createContext<OperatorConfigContextValue | undefined>(undefined);

export function OperatorConfigProvider({ children }: { children: ReactNode }) {
  const defaults = useMemo(() => createDefaults(), []);

  const [config, setConfig] = useState<OperatorConfig>(() => {
    if (typeof window === 'undefined') {
      return defaults;
    }
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<OperatorConfig>;
        return { ...defaults, ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load operator config from storage', error);
    }
    return defaults;
  });

  useEffect(() => {
    setBackendBaseUrl(config.backendUrl);
  }, [config.backendUrl]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch (error) {
      console.warn('Failed to persist operator config', error);
    }
  }, [config]);

  const updateConfig = useCallback((update: Partial<OperatorConfig>) => {
    setConfig((prev) => ({ ...prev, ...update }));
  }, []);

  const resetConfig = useCallback(() => {
    setConfig(defaults);
  }, [defaults]);

  const value = useMemo<OperatorConfigContextValue>(
    () => ({ config, defaults, updateConfig, resetConfig }),
    [config, defaults, updateConfig, resetConfig],
  );

  return <OperatorConfigContext.Provider value={value}>{children}</OperatorConfigContext.Provider>;
}

export function useOperatorConfig() {
  const context = useContext(OperatorConfigContext);
  if (!context) {
    throw new Error('useOperatorConfig must be used within an OperatorConfigProvider');
  }
  return context;
}
